import { useEffect, useMemo, useState } from "react";
import {
  siListFormulas, siCreateFormula, siUpdateFormula, siDeleteFormula, siReorderFormulas, siListQuestions,
} from "../lib/smartInspectApi";
import { getRoiModel } from "../lib/adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown, Loader2, X, Sigma } from "lucide-react";

const CATEGORIES = ["Volume", "Process", "Savings", "Investment", "Results", "Other"];
const DEP_TYPES = [
  { value: "question", label: "Question" },
  { value: "assumption", label: "ROI Assumption" },
  { value: "constant", label: "Constant" },
];
const DEP_LABEL = { question: "Question", assumption: "ROI Assumption", constant: "Constant" };
const DEP_STYLE = {
  question: "bg-[#E0F2FE] text-[#025979]",
  assumption: "bg-[#ECFDF5] text-[#065F46]",
  constant: "bg-[#FEF3C7] text-[#92400E]",
};
const SI_MODEL_KEY = "smartinspect_inspection_roi";

const EMPTY = {
  formula_name: "", output_variable: "", category: "Other", description: "",
  expression: "", dependencies: [], status: "active", display_order: 0,
};

function StatusBadge({ status }) {
  const active = status === "active";
  return (
    <span className={`inline-block px-1.5 py-0.5 rounded-sm text-[9px] font-semibold ${active ? "bg-[#DCFCE7] text-[#166534]" : "bg-[#F3F4F6] text-[#9CA3AF]"}`}>
      {active ? "Active" : "Inactive"}
    </span>
  );
}

export default function SmartInspectRoiFormulasPage() {
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  // Dependency option sources (read-only reference lists).
  const [questionOpts, setQuestionOpts] = useState([]);
  const [assumptionOpts, setAssumptionOpts] = useState([]);
  // New-dependency builder state (inside the editor).
  const [depType, setDepType] = useState("question");
  const [depRef, setDepRef] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const rows = await siListFormulas();
      setFormulas(rows);
    } catch { toast.error("Failed to load formula mappings"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    // Build dependency option lists from Questions + the SmartInspect ROI Model.
    siListQuestions()
      .then((qs) => setQuestionOpts((qs || []).map((q) => ({ value: q.field_key, label: `${q.label} (${q.field_key})` }))))
      .catch(() => {});
    getRoiModel(SI_MODEL_KEY)
      .then((m) => {
        const keys = [];
        ["general_assumptions", "cost_drivers", "investment_assumptions", "risk_assumptions", "advanced_assumptions"]
          .forEach((g) => Object.keys(m?.[g] || {}).forEach((k) => keys.push(k)));
        (m?.benefit_categories || []).forEach((b) => keys.push(b.key));
        setAssumptionOpts(Array.from(new Set(keys)).map((k) => ({ value: k, label: k })));
      })
      .catch(() => {});
  }, []);

  const sorted = useMemo(
    () => [...formulas].sort((a, b) => (a.display_order || 0) - (b.display_order || 0)),
    [formulas],
  );

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= sorted.length) return;
    const a = sorted[idx], b = sorted[j];
    const ao = a.display_order || 0, bo = b.display_order || 0;
    setFormulas((prev) => prev.map((x) => x.id === a.id ? { ...x, display_order: bo } : x.id === b.id ? { ...x, display_order: ao } : x));
    try { await siReorderFormulas([{ id: a.id, display_order: bo }, { id: b.id, display_order: ao }]); }
    catch { toast.error("Reorder failed"); load(); }
  };

  const openNew = () => {
    setDepType("question"); setDepRef("");
    setEditing({ ...EMPTY, dependencies: [], display_order: (sorted.length || 0) * 10 + 10 });
  };
  const openEdit = (f) => {
    setDepType("question"); setDepRef("");
    setEditing({ ...f, dependencies: Array.isArray(f.dependencies) ? f.dependencies : [] });
  };

  const addDependency = () => {
    if (!depRef || !String(depRef).trim()) return;
    const dep = { type: depType, ref: String(depRef).trim() };
    setEditing((e) => ({ ...e, dependencies: [...(e.dependencies || []), dep] }));
    setDepRef("");
  };
  const removeDependency = (i) => setEditing((e) => ({ ...e, dependencies: e.dependencies.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!editing.formula_name.trim() || !editing.output_variable.trim()) {
      toast.error("Formula name and output variable are required."); return;
    }
    setSaving(true);
    try {
      const payload = { ...editing, output_variable: editing.output_variable.trim() };
      if (editing.id) {
        const u = await siUpdateFormula(editing.id, payload);
        setFormulas((prev) => prev.map((x) => (x.id === editing.id ? u : x)));
        toast.success("Formula updated");
      } else {
        const c = await siCreateFormula(payload);
        setFormulas((prev) => [...prev, c]);
        toast.success("Formula created");
      }
      setEditing(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await siDeleteFormula(deleteTarget.id);
      setFormulas((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success("Deleted");
    } catch { toast.error("Delete failed"); }
    finally { setDeleteTarget(null); }
  };

  const refOptions = depType === "question" ? questionOpts : depType === "assumption" ? assumptionOpts : [];

  return (
    <div data-testid="si-roi-formulas-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h1 className="text-xl font-bold text-[#111827]" style={{ fontFamily: "Chivo" }}>SmartInspect ROI Formula Mapping</h1>
        <Button data-testid="si-add-formula-btn" onClick={openNew} className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs">
          <Plus className="w-3.5 h-3.5 mr-1" /> Add Formula
        </Button>
      </div>
      <p className="text-xs text-[#9CA3AF] mb-4 max-w-3xl leading-relaxed">
        Configure how customer answers and ROI assumptions combine to produce ROI calculations. Formulas and dependencies are stored as configuration only — nothing is executed here.
      </p>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="animate-spin text-[#025979]" /></div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] rounded-sm shadow-sm overflow-hidden" data-testid="si-formula-table">
          <div className="px-4 py-3 bg-gray-50 border-b border-[#E5E7EB] flex items-center justify-between">
            <p className="text-xs font-medium text-[#111827] flex items-center gap-1.5"><Sigma className="w-3.5 h-3.5 text-[#025979]" /> Formula Mappings</p>
            <p className="text-[10px] text-[#9CA3AF]">{sorted.length} formula{sorted.length !== 1 ? "s" : ""}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-white">
                  <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Order</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium min-w-[220px]">Formula Name</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-52">Output Variable</th>
                  <th className="text-left px-3 py-2 text-[#6B7280] font-medium w-28">Category</th>
                  <th className="text-center px-2 py-2 text-[#6B7280] font-medium w-16">Status</th>
                  <th className="text-right px-3 py-2 text-[#6B7280] font-medium w-16">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="si-formula-tbody">
                {sorted.map((f, idx) => (
                  <tr key={f.id} data-testid={`si-formula-row-${f.output_variable}`} className="border-b border-[#E5E7EB] hover:bg-gray-50 transition-colors align-top">
                    <td className="px-2 py-2.5">
                      <div className="flex items-center justify-center gap-0.5">
                        <span className="text-[#9CA3AF] text-[10px] w-4 text-center">{idx + 1}</span>
                        <div className="flex flex-col">
                          <button data-testid={`si-formula-moveup-${f.output_variable}`} onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB] disabled:cursor-not-allowed" title="Move up"><ArrowUp className="w-3 h-3" /></button>
                          <button data-testid={`si-formula-movedown-${f.output_variable}`} onClick={() => move(idx, 1)} disabled={idx === sorted.length - 1} className="text-[#6B7280] hover:text-[#025979] disabled:text-[#E5E7EB] disabled:cursor-not-allowed" title="Move down"><ArrowDown className="w-3 h-3" /></button>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 py-2.5 text-[#111827]">
                      <div className="leading-snug font-medium">{f.formula_name}</div>
                      {f.description && <div className="text-[10px] text-[#9CA3AF] mt-0.5 max-w-md leading-relaxed">{f.description}</div>}
                    </td>
                    <td className="px-3 py-2.5"><span className="font-mono text-[10px] text-[#475569] bg-[#F1F5F9] border border-[#E5E7EB] rounded px-1.5 py-0.5">{f.output_variable}</span></td>
                    <td className="px-3 py-2.5 text-[10px] text-[#6B7280]">{f.category}</td>
                    <td className="px-2 py-2.5 text-center"><StatusBadge status={f.status} /></td>
                    <td className="px-3 py-2.5 text-right whitespace-nowrap">
                      <button data-testid={`si-formula-edit-${f.output_variable}`} onClick={() => openEdit(f)} className="text-[#025979] hover:text-[#03738C] mr-2" title="Edit"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`si-formula-delete-${f.output_variable}`} onClick={() => setDeleteTarget(f)} className="text-red-400 hover:text-red-600" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
                {sorted.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-[#9CA3AF]">
                      No formula mappings yet.
                      <button onClick={openNew} className="text-[#025979] hover:text-[#03738C] ml-1 font-medium">Add one</button>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Formula Editor */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="si-formula-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Edit Formula" : "New Formula"}</DialogTitle>
            <DialogDescription>Define the mapping relationship. Expressions are stored as configuration only and are not executed in this screen.</DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Field label="Formula Name" span>
                <Input data-testid="si-formula-name" value={editing.formula_name} onChange={(e) => setEditing({ ...editing, formula_name: e.target.value })} />
              </Field>
              <Field label="Output Variable (must be unique)">
                <Input data-testid="si-formula-output" value={editing.output_variable} onChange={(e) => setEditing({ ...editing, output_variable: e.target.value })} />
              </Field>
              <Field label="Category">
                <Select value={editing.category} onValueChange={(v) => setEditing({ ...editing, category: v })}>
                  <SelectTrigger data-testid="si-formula-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Description" span>
                <Input value={editing.description || ""} onChange={(e) => setEditing({ ...editing, description: e.target.value })} />
              </Field>
              <Field label="Formula Expression (stored only — not evaluated)" span>
                <Textarea data-testid="si-formula-expression" rows={3} className="font-mono text-xs" value={editing.expression || ""} onChange={(e) => setEditing({ ...editing, expression: e.target.value })} />
              </Field>

              {/* Dependencies builder */}
              <div className="col-span-2">
                <label className="block text-xs font-medium text-slate-500 mb-1">Dependencies</label>
                <p className="text-[10px] text-[#9CA3AF] mb-2">Read-only references used by this formula. Choose a Question field, ROI Assumption, or a Constant.</p>
                <div className="flex items-end gap-2 flex-wrap mb-2">
                  <div className="w-36">
                    <Select value={depType} onValueChange={(v) => { setDepType(v); setDepRef(""); }}>
                      <SelectTrigger data-testid="si-dep-type" className="text-xs h-8"><SelectValue /></SelectTrigger>
                      <SelectContent>{DEP_TYPES.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    {depType === "constant" ? (
                      <Input data-testid="si-dep-ref-input" className="text-xs h-8" placeholder="e.g. 60" value={depRef} onChange={(e) => setDepRef(e.target.value)} />
                    ) : (
                      <Select value={depRef} onValueChange={setDepRef}>
                        <SelectTrigger data-testid="si-dep-ref-select" className="text-xs h-8"><SelectValue placeholder={`Select a ${DEP_LABEL[depType].toLowerCase()}…`} /></SelectTrigger>
                        <SelectContent className="max-h-64">{refOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                      </Select>
                    )}
                  </div>
                  <Button type="button" data-testid="si-dep-add-btn" size="sm" variant="outline" onClick={addDependency}>
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5" data-testid="si-dep-list">
                  {(editing.dependencies || []).length === 0 && <span className="text-[11px] text-[#9CA3AF] italic">No dependencies added.</span>}
                  {(editing.dependencies || []).map((d, i) => (
                    <span key={`${d.type}-${d.ref}-${i}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${DEP_STYLE[d.type] || DEP_STYLE.constant}`}>
                      <span className="opacity-70">{DEP_LABEL[d.type] || d.type}:</span> <span className="font-mono">{d.ref}</span>
                      <button type="button" onClick={() => removeDependency(i)} className="hover:text-red-600"><X className="w-2.5 h-2.5" /></button>
                    </span>
                  ))}
                </div>
              </div>

              <Field label="Status">
                <Select value={editing.status} onValueChange={(v) => setEditing({ ...editing, status: v })}>
                  <SelectTrigger data-testid="si-formula-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button data-testid="si-formula-save" onClick={save} disabled={saving}>{saving ? <Loader2 size={16} className="animate-spin mr-1" /> : null} Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="si-formula-delete-dialog">
          <DialogHeader>
            <DialogTitle>Delete Formula</DialogTitle>
            <DialogDescription>Delete "{deleteTarget?.formula_name}"? This removes the mapping configuration. This action cannot be undone.</DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="rounded-sm text-xs">Cancel</Button>
            <Button data-testid="si-formula-confirm-delete" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-sm text-xs">Delete Formula</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, children, span }) {
  return (
    <div className={span ? "col-span-2" : ""}>
      <label className="block text-xs font-medium text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
